import type { ParsedMailLogRow } from '../types/utils'
/** Normalize mail trace / mail_logs rows for the conversation-history modal (Angular `EmailParserDetailComponent`). */


function asRecord(row: unknown): Record<string, unknown> | null {
  return row && typeof row === "object" ? (row as Record<string, unknown>) : null;
}

function pickString(rec: Record<string, unknown>, keys: string[]): string {
  for (const key of keys) {
    const v = rec[key];
    if (typeof v === "string" && v.trim()) return v;
    if (typeof v === "number") return String(v);
  }
  return "";
}

/** Prefer HTML-ish fields for iframe rendering (Angular writes `body` into iframe). */
export function buildIframeSrcDoc(htmlOrBody: string): string {
  const raw = htmlOrBody.trim();
  if (!raw) {
    return `<!DOCTYPE html><html><head><meta charset="utf-8"><base target="_blank"></head><body><p style="font-family:system-ui,sans-serif;color:#64748b;">No body content.</p></body></html>`;
  }
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><base target="_blank"><meta name="referrer" content="no-referrer"></head><body>${raw}</body></html>`;
}

/** Angular EmailParserDetailComponent filter checkbox keys. */
export type MailConversationFilterKey = "forapplicant" | "showreply";

/**
 * Angular `email-parser-detail` visibility (`d-none` when false):
 * hide when `filterArray.length !== 2` and
 * (`showreply` && source !== imap) or (`forapplicant` && to !== applicant email).
 * When both filters are checked, Angular shows every row.
 */
export function isMailLogVisibleUnderFilters(
  filters: readonly MailConversationFilterKey[],
  mail: { source: string; to: string },
  applicantEmail: string,
): boolean {
  if (filters.length === 2) return true;
  if (filters.includes("showreply") && mail.source !== "imap") return false;
  if (
    filters.includes("forapplicant") &&
    mail.to !== applicantEmail
  ) {
    return false;
  }
  return true;
}

export function toggleMailConversationFilter(
  filters: readonly MailConversationFilterKey[],
  key: MailConversationFilterKey,
): MailConversationFilterKey[] {
  if (filters.includes(key)) {
    return filters.filter((f) => f !== key);
  }
  return [...filters, key];
}

export function parseMailLogRow(row: unknown, index: number): ParsedMailLogRow {
  const rec = asRecord(row) ?? {};
  const id =
    pickString(rec, ["id", "mail_id", "email_id"]) || `idx-${index}`;
  const htmlBody = pickString(rec, [
    "body",
    "html",
    "email_body",
    "message",
    "content",
    "mail_body",
  ]);
  return {
    id,
    to: pickString(rec, ["to", "to_email", "email"]),
    subject: pickString(rec, ["subject", "email_type"]),
    createdAt: pickString(rec, ["created_at", "sent_at", "date"]),
    htmlBody,
    source: pickString(rec, ["source"]).toLowerCase() || "unknown",
  };
}
